import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UsersApi } from "@/openapi/generated";
import type {
  PaginatedResultOfGetUserResponse,
  ScrollUserRequest,
  SearchQueryRequest,
} from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUseUsersScrollGetProps {
  page?: number;
  pageSize?: number;
  active?: boolean;
  lastSelectedId?: number | null;
  /** Filter by role names (e.g. ["Member"] to fetch members) */
  roleNames?: string[];
  /** Filter by parent/user location IDs */
  locationIds?: number[];
  /** Parents with students in these seasons */
  studentSeasonIds?: number[];
  /** Parents with students at these locations */
  studentLocationIds?: number[];
  /** When false, the request is not sent */
  enabled?: boolean;
  /** Keep showing the previous page while a new query is loading */
  keepPreviousData?: boolean;
  /** Optional server-side search query */
  searchQueryRequest?: SearchQueryRequest | null;
  /** Full scroll body; when set, overrides individual scroll fields above */
  scrollUserRequest?: ScrollUserRequest;
}

export const useUsersScrollGet = ({
  page = 1,
  pageSize = 100,
  active,
  lastSelectedId,
  roleNames = [],
  locationIds = [],
  studentSeasonIds = [],
  studentLocationIds = [],
  enabled = true,
  keepPreviousData = false,
  searchQueryRequest,
  scrollUserRequest,
}: IUseUsersScrollGetProps = {}) => {
  const accessToken = useAccessToken();

  const resolvedScrollRequest: ScrollUserRequest =
    scrollUserRequest ?? {
      studentLocationIds,
      studentSeasonIds,
      locationIds,
      roleNames,
      active: active ?? null,
      searchQuery: searchQueryRequest ?? null,
    };

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch, error } = useQuery({
    queryKey: [
      QueryKeys.UsersScrollGet,
      page,
      pageSize,
      active,
      lastSelectedId,
      resolvedScrollRequest,
      enabled,
    ],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(UsersApi, accessToken);
      return service.usersScrollPost({
        scrollUserRequest: resolvedScrollRequest,
        page,
        pageSize,
        lastSelectedId: lastSelectedId ?? undefined,
        active,
      });
    },
    enabled: !!accessToken && enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...(keepPreviousData
      ? {
          placeholderData: (
            previousData: PaginatedResultOfGetUserResponse | undefined,
          ) => previousData,
        }
      : {}),
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
    error,
  };
};
